<script lang="ts">
    import { createEventDispatcher } from 'svelte'
    import ZinputExpander from './zinput-expander.svelte'

    export let value: any[] = []
    export let options: { value: any; label: string; group?: string; disabled?: boolean }[] = []
    export let disabled = false
    export let labeled = false
    export let label = ''
    export let htmlInput = null
    export let hideNested: boolean = false

    const dispatch = createEventDispatcher()

    let selectOptions: { group: string; options: { value: any; label: string; disabled?: boolean }[] }[] = []

    $: loadOptions(options)

    function loadOptions(opts) {
        const grouped: typeof selectOptions = []
        for (const option of opts || []) {
            const found = grouped.find((el) => el.group === option.group)
            if (found) {
                found.options.push(option)
            } else {
                grouped.push({ group: option.group, options: [option] })
            }
        }
        selectOptions = grouped
    }

    $: safeValue = Array.isArray(value) ? value : []

    $: selectedLabel = safeValue.length === 0 ? '' : safeValue.length === 1 ? '1 opção selecionada' : `${safeValue.length} opções selecionadas`

    function isSelected(v, sel) {
        return sel.includes(v)
    }

    function groupValues(group) {
        return group.options.filter((o) => !o.disabled).map((o) => o.value)
    }

    function groupState(group, sel) {
        const values = groupValues(group)
        if (values.length === 0) return 'none'
        const selectedCount = values.filter((v) => sel.includes(v)).length
        if (selectedCount === 0) return 'none'
        if (selectedCount === values.length) return 'all'
        return 'partial'
    }

    function toggleOption(opt) {
        if (disabled || opt.disabled) return
        if (isSelected(opt.value, safeValue)) {
            value = safeValue.filter((v) => v !== opt.value)
        } else {
            value = [...safeValue, opt.value]
        }
        dispatch('change')
    }

    function toggleGroup(group) {
        if (disabled) return
        const values = groupValues(group)
        if (values.length === 0) return
        if (groupState(group, safeValue) === 'all') {
            value = safeValue.filter((v) => !values.includes(v))
        } else {
            value = [...new Set([...safeValue, ...values])]
        }
        dispatch('change')
    }

    function onRowKeydown(ev, action) {
        if (ev.key === 'Enter' || ev.key === ' ') {
            ev.preventDefault()
            action()
        }
    }

    function indeterminateAction(node, isPartial) {
        node.indeterminate = isPartial
        return {
            update(v) {
                node.indeterminate = v
            },
        }
    }
</script>

<ZinputExpander titulo={label} {disabled} {labeled} bind:htmlInput>
    <svelte:fragment slot="trigger">
        <span class="groupselect_trigger_text">{selectedLabel}</span>
    </svelte:fragment>
    <svelte:fragment slot="panel">
        {#each selectOptions as group}
            {#if group.group}
                <div
                    class="groupselect_group"
                    role="checkbox"
                    aria-checked={groupState(group, safeValue) === 'all'}
                    tabindex="0"
                    on:click={() => toggleGroup(group)}
                    on:keydown={(ev) => onRowKeydown(ev, () => toggleGroup(group))}
                >
                    <input
                        type="checkbox"
                        class="form-check-input"
                        checked={groupState(group, safeValue) === 'all'}
                        use:indeterminateAction={groupState(group, safeValue) === 'partial'}
                        tabindex="-1"
                    />
                    {group.group}
                </div>
            {/if}
            {#if !hideNested}
                {#each group.options as opt (opt.value)}
                    <div
                        class="groupselect_option"
                        class:groupselect_option-nested={!!group.group}
                        class:disabled={opt.disabled}
                        role="checkbox"
                        aria-checked={isSelected(opt.value, safeValue)}
                        tabindex="0"
                        on:click={() => toggleOption(opt)}
                        on:keydown={(ev) => onRowKeydown(ev, () => toggleOption(opt))}
                    >
                        <input
                            type="checkbox"
                            class="form-check-input"
                            checked={isSelected(opt.value, safeValue)}
                            disabled={opt.disabled}
                            tabindex="-1"
                        />
                        {opt.label}
                    </div>
                {/each}
            {/if}
        {/each}
    </svelte:fragment>
</ZinputExpander>

<style>
    .groupselect_trigger_text {
        flex-grow: 1;
        padding-left: 8px;
    }

    .groupselect_group {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 2px 12px;
        cursor: pointer;
        font-weight: 600;
        opacity: 0.9;
    }

    .groupselect_group input {
        margin-top: 0px;
    }

    .groupselect_option input {
        margin-top: 0px;
    }

    .groupselect_option {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 0px 12px;
        cursor: pointer;
    }

    .groupselect_option-nested {
        padding-left: 37px;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
    }

    .groupselect_group:hover,
    .groupselect_option:hover {
        background-color: #ffffff12;
    }

    :global(.theme_light) .groupselect_group:hover,
    :global(.theme_light) .groupselect_option:hover {
        background-color: #00000010;
    }

    .groupselect_option.disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
</style>
